import React, { useEffect, useState, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { useWallet } from 'use-wallet'
import { Helmet } from 'react-helmet'
import { ToastContainer, toast } from 'react-toastify'
import Spinner from 'react-svg-spinner'
import 'react-toastify/dist/ReactToastify.css'
import { toUtf8 } from 'web3-utils'
import Web3 from 'web3'
import ReactMarkdown from 'react-markdown'
import gfm from 'remark-gfm'

import PageHeader from 'components/PageHeader'
import Spacer from 'components/Spacer'
import Button from 'components/Button'
import Input from 'components/Input'
import Label from 'components/Label'
import ApproveTransferModal from 'components/ApproveTransferModal'
import WalletProviderModal from 'components/WalletProviderModal'
import CreateNewPoolModal from 'components/CreateNewPoolModal'
import EditPoolIntrolModal from 'components/EditPoolIntrolModal'
import WalletUnlockButton from 'components/WalletUnlockButton'
import Tooltip from 'components/Tooltip'

import { AccountContext } from 'contexts/AccountProvider'
import {
  weiToEth,
  viewTranscationURL,
  getERC20Token,
  WETH,
} from 'lottery/constants'
import { PoolListContext } from 'contexts/PoolListProvider'
import {
  formatValue,
  getPoolRewardText,
  getPoolTokenDecimal,
  getPoolTokenSymbol,
  getPoolTokenLogoURL,
  getTotalBonusTickets,
  getTokenMetaFromAddress,
} from 'lottery/util'
import useAllowance from 'hooks/useAllowance'
import useModal from 'hooks/useModal'
import { getPoolIntroById, getPoolIntroByIdFromJSON } from 'api'
import edit from 'assets/img/edit.png'
import share from 'assets/img/share.png'


const Pool = () => {
  const { poolId } = useParams()
  const { account, ethereum } = useWallet()
  const { poolList, fetchPoolList } = useContext(PoolListContext)
  const allowance = useAllowance()
  const history = useHistory()
  const { poolsContract, getAllowance, getContractOwner } = useContext(
    AccountContext
  )
  const [purchasedAmount, setPurchasedAmount] = useState(1)
  const [isPurchasable, setIsPurchasable] = useState(true)
  const [errMessage, setErrMessage] = useState('')
  const [isDrawable, setIsDrawable] = useState(false)
  const [selectedPool, setSelectedPool] = useState(null)
  const [loading, setLoading] = useState(false)
  const [renewExpireDate, setRenewExpireDate] = useState()
  const [currencyTokenName, setCurrencyTokenName] = useState('')
  const [tokenName, setTokenName] = useState('')
  const [currencyTokenDecimal, setCurrencyTokenDecimal] = useState(0)
  const [tokenDecimal, setTokenDecimal] = useState(0)
  const [contractOwner, setContractOwner] = useState('')
  const [poolRewardText, setPoolRewardText] = useState('')
  const [poolIntro, setPoolIntro] = useState(null)

  let hashLink = ''

  const [onPresentWalletProviderModal] = useModal(<WalletProviderModal />)
  const [onPresetApproveTranferModal] = useModal(
    <ApproveTransferModal
      ERC20TokenAddress={selectedPool ? selectedPool.currencyToken : null}
    />
  )

  const [onPresentEditPoolIntrolModal] = useModal(
    <EditPoolIntrolModal
      title={poolIntro || `Get ${tokenName} tokens`}
      poolId={selectedPool?.pid}
      setPoolIntro={setPoolIntro}
    />
  )

  const [onPresentApproveTransferModal] = useModal(<ApproveTransferModal />)
  const [onPresentCreateNewPoolModal] = useModal(
    <CreateNewPoolModal history={history} />
  )

  let poolData = poolList.find((item) => item.pid == poolId)
  const expireDate = new Date(poolData?.expiry * 1000)
  const currentDate = new Date()

  const res = (expireDate - currentDate) / 1000
  const restExpireDays = Math.floor(res / 86400)
  useEffect(() => {
    if (poolsContract) {
      getContracts()
      setRenewExpireDate(parseInt(poolData?.expiry) + parseInt(2592000))
      getPoolIntroByIdFromJSON(poolId)
        .then((res) => {
          setPoolIntro(res)
        })
        .catch((err) => {
          console.log('err: ', err)
          getPoolIntroById(poolId)
            .then((res) => {
              if (res?.result) {
                !!res.response && setPoolIntro(res.response)
              }
            })
            .catch((err) => {
              console.log('err: ', err)
          })
        })
    }
  }, [poolsContract, account])

  useEffect(() => {
    if (poolData) {
      getTokenInfo()
    } else {
      fetchPoolList()
    }
  }, [poolData])

  const isValidAddress = (address) => {
    const web3 = new Web3(ethereum)
    return web3.utils.isAddress(address)
  }

  const getTokenInfo = async () => {
    const currencyToken = await getERC20Token(ethereum, poolData.currencyToken)
    console.log('currencyToken')
    const tokenName = await currencyToken.methods.symbol().call()
    const currencyTokendecimal = await currencyToken.methods.decimals().call()
    setCurrencyTokenName(tokenName)
    setCurrencyTokenDecimal(currencyTokendecimal)
    const token = await getERC20Token(ethereum, poolData.token)
    const tName = await token.methods.symbol().call()
    const tokenDecimal = await token.methods.decimals().call()
    setTokenDecimal(tokenDecimal)
    setTokenName(tName)
    if (getTokenMetaFromAddress(poolData.token)) {
      setPoolRewardText(getPoolRewardText(poolData))
    } else {
      setPoolRewardText(`${getPoolRewardText(poolData)} ${tName}`)
    }
  }
  const getLatestName = async (lotteryPool, pid) => {
    const nameEvents = await lotteryPool.getPastEvents('PoolNameChange', {
      filter: {
        poolId: pid.toString(),
      },
      fromBlock: 1,
      toBlock: 'latest',
    })
    let nameEvent = await nameEvents.pop()
    let name = 'you bet!'
    let transactionHash = ''
    if (nameEvent !== undefined) {
      name = toUtf8(nameEvent.returnValues.name)
      transactionHash = nameEvent.transactionHash
    }
    return { name, transactionHash }
  }

  const getContracts = async () => {
    const contractOwner = await getContractOwner()
    contractOwner && setContractOwner(contractOwner)

    const pool = await poolsContract.methods.pools(poolId).call()
    const {
      currencyToken,
      token,
      balance,
      reward,
      weight,
      owner,
      size,
      tickets,
      weightBalance,
      expiry,
      status,
    } = pool

    const { name, transactionHash } = await getLatestName(poolsContract, poolId)
    const lotteryPool = {
      pid: poolId,
      token,
      currencyToken,
      balance,
      reward,
      weight,
      owner,
      size,
      tickets,
      weightBalance,
      expiry,
      status,
      name,
      transactionHash,
    }

    if (lotteryPool) {
      if (lotteryPool.name === undefined || lotteryPool.name === '') {
        lotteryPool.name = 'You Bet!'
      }
      setSelectedPool(lotteryPool)
      if (lotteryPool.status !== '1') setIsPurchasable(false)
      const isDrawableTmp =
        lotteryPool.status === '1' &&
        lotteryPool.balance / lotteryPool.reward >= 1.2
      setIsDrawable(isDrawableTmp)
      if (poolsContract) {
        setIsDrawable(true)
        // getContractConfig()
      }
    }
  }

  // const getContractConfig = async () => {
  //   const poolThreshold = await poolsContract.methods.poolThreshold().call()
  //   const contract = await poolsContract.methods.pools(poolId).call()
  //   const isDrawable =
  //     contract.balance / contract.reward >= poolThreshold / 100000
  //   setIsDrawable(isDrawable)
  // }

  const handleDraw = () => {
    poolsContract.methods
      .closePool(BigNumber(poolId).toFixed(0))
      .send({ from: account })
      .on('transactionHash', (tx) => {
        return tx.transactionHash
      })
  }

  const transectionSuccess = () => {
    return (
      <div>
        Your purchase transaction went through successfully!{' '}
        <a href={hashLink} rel="noreferrer" target="_blank">
          View Transaction
        </a>
      </div>
    )
  }

  const handleRenew = async () => {
    poolsContract.methods
      .renewPool(poolId, selectedPool.weight, renewExpireDate)
      .send({ from: account })
      .on('transactionHash', (tx) => {
        getContracts()
        /*  history.push(`/`)
        history.push(`/pools/${poolId}`)
     */
        hashLink = viewTranscationURL + tx
        return tx
      })
      .on('confirmation', (confirmationNumber, receipt) => {
        if (confirmationNumber === 2 && receipt.status === true) {
          toast(transectionSuccess)
          setLoading(false)
        }
      })
  }

  const handlePurchase = async () => {
    if (!account) {
      onPresentWalletProviderModal()
      return
    }

    const allowance = await getAllowance(selectedPool.currencyToken)
    if (
      selectedPool.currencyToken.toLowerCase() !== WETH &&
      (!allowance || allowance === '0')
    ) {
      onPresetApproveTranferModal()
      return
    }
    setIsPurchasable(false)
    /*   if (Number(tickets) > 20) {
      setTickets(1)
      setErrMessage('The tickets should be less than 20.')
      setIsPurchasable(true)
      return
    } */

    if (Number(purchasedAmount) == 0) {
      setPurchasedAmount(1)
      setErrMessage('The minimum amount of tokens you can purchase is 1')
      setIsPurchasable(true)
      return
    }

    setLoading(true)
    const referrer = localStorage.getItem('referrer')
    if (selectedPool.currencyToken.toLowerCase() === WETH) {
      poolsContract.methods
        .buyTokenWithETH(
          BigNumber(poolId).toFixed(0),
          BigNumber(purchasedAmount * Math.pow(10, tokenDecimal)).toFixed(0),
          isValidAddress(referrer)
            ? referrer
            : '0x0000000000000000000000000000000000000000'
        )
        .send({
          from: account,
          value: BigNumber(poolData.exchangeRate)
            .times(BigNumber(purchasedAmount * Math.pow(10, tokenDecimal)))
            .div(BigNumber(1e18)),
        })
        .on('transactionHash', (tx) => {
          hashLink = viewTranscationURL + tx
          // return tx
        })
        .on('confirmation', (confirmationNumber, receipt) => {
          if (confirmationNumber === 2 && receipt.status === true) {
            toast(transectionSuccess)
            setLoading(false)
          }
        })
    } else {
      poolsContract.methods
        .buyToken(
          BigNumber(poolId).toFixed(0),
          BigNumber(purchasedAmount * Math.pow(10, tokenDecimal)).toFixed(0),
          isValidAddress(referrer)
            ? referrer
            : '0x0000000000000000000000000000000000000000'
        )
        .send({ from: account })
        .on('transactionHash', (tx) => {
          hashLink = viewTranscationURL + tx
          // return tx
        })
        .on('confirmation', (confirmationNumber, receipt) => {
          if (confirmationNumber === 2 && receipt.status === true) {
            toast(transectionSuccess)
            setLoading(false)
          }
        })
    }

    setIsPurchasable(true)
  }

  const handleChangeTickets = (e) => {
    setErrMessage('')
    setPurchasedAmount(e.target.value)
  }

  const handleGoHome = () => {
    history.push('/')
  }

  const handleEditPoolIntroModal = () => {
    onPresentEditPoolIntrolModal()
  }

  const handleCreateNewPool = async () => {
    if (!account) {
      onPresentWalletProviderModal()
      return
    }

    if (!allowance.toNumber()) {
      onPresentApproveTransferModal()
      return
    }

    onPresentCreateNewPoolModal()
    return
  }

  const getTotalCost = () => {
    if (tokenDecimal) {
      return (
        (purchasedAmount * weiToEth(BigNumber(poolData.exchangeRate))) /
        Math.pow(10, tokenDecimal - currencyTokenDecimal)
      )
    }
    return 0
  }

  const getExchangeRate = () => {
    if (tokenDecimal) {
      return (
        weiToEth(BigNumber(poolData.exchangeRate)) /
        Math.pow(10, tokenDecimal - currencyTokenDecimal)
      )
    }

    return 0
  }

  if (!account) {
    return (
      <>
        <WalletUnlockButton handleUnlockClick={handleCreateNewPool} />
      </>
    )
  }

  if (!selectedPool) {
    return (
      <>
        <StyledInfo>There is no Contract.</StyledInfo>
      </>
    )
  }

  const rewardValue = getPoolRewardText(selectedPool).includes(tokenName) ?
    getPoolRewardText(selectedPool) : `${getPoolRewardText(selectedPool)} ${tokenName}`

  return (
    <div>
      <Helmet>
        <title>
          {selectedPool.name}! Enter to win {poolRewardText}
        </title>
        <meta
          name="description"
          content={`${selectedPool.name}! Get ${tokenName} tokens`}
        />

        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/** Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${selectedPool.name}! Get ${tokenName} tokens`}
        />
        <meta
          property="og:description"
          content={`${selectedPool.name}! Get ${tokenName} tokens`}
        />
        <meta property="og:image" content={getPoolTokenLogoURL(selectedPool)} />
        <meta
          property="og:image:secure_url"
          content={getPoolTokenLogoURL(selectedPool)}
        />
        <meta property="og:image:alt" content="Token Logo" />

        {/** Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta
          property="twitter:title"
          content={`${selectedPool.name}! Get ${tokenName} tokens`}
        />
        <meta
          property="twitter:description"
          content={`${selectedPool.name}! Get ${tokenName} tokens`}
        />
        <meta
          property="twitter:image"
          content={getPoolTokenLogoURL(selectedPool)}
        />
      </Helmet>
      <PageHeader
        icon={
          <img
            src={getPoolTokenLogoURL(selectedPool)}
            onClick={handleGoHome}
            height="120"
            alt="dice"
          />
        }
        title={selectedPool.name}
        subtitle={
          <ReactMarkdown linkTarget="_blank" plugins={[gfm]}>
            {poolIntro || `Get ${tokenName} tokens`}
          </ReactMarkdown>
        }
        editable={
          account == contractOwner ||
          (account == selectedPool.owner &&
            Number(
              selectedPool.weight.slice(0, selectedPool.weight.length - 18)
            ) >= 100)
        }
        isShare={true}
        tooltip="Every time you refer a sale, you'll get 1% commission of the order. <br />
          If you referred the most sale in this launch when it closes, you'd get 1% of its entire proceeds. <br />
          Click here to copy your referral URL and share it to start earning commissions."
        edit={
          <img
            src={edit}
            onClick={handleEditPoolIntroModal}
            alt="Edit"
            height="20"
            width="20"
          />
        }
        token={selectedPool.token}
      />

      <StyledPool>
        <Input
          startAdornment="Purchase"
          endAdornment={`${tokenName} at ${getExchangeRate()} ${currencyTokenName} each`}
          placeholder="Amount"
          value={purchasedAmount}
          onChange={handleChangeTickets}
        />
        <Spacer />
        {errMessage && (
          <div>
            <StyledErrMessage>{errMessage}</StyledErrMessage>
            <Spacer />
          </div>
        )}
        <Label text={`Total cost: ${formatValue(getTotalCost())} ${currencyTokenName}  `} />
        <Label
          text={`Available for purchase: ${rewardValue} `}
        />
        <Spacer />
        {loading ? (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <Spinner />
            <Label size="12" text="Waiting for the transaction" />
            <Spacer />
          </div>
        ) : (
          <></>
        )}

        {restExpireDays > 0 ? (
          <>
            {restExpireDays >= 0 && restExpireDays < 7 ? (
              <>
                <Label text={`About to expire in ${restExpireDays} days`} />
                <Spacer size="md" />
                <StyledCardsWrapper>
                  <Button
                    text="Purchase"
                    disabled={!isPurchasable}
                    onClick={handlePurchase}
                  />
                  {account == selectedPool.owner || account == contractOwner ? (
                    <Button
                      text="Conclude the Sale"
                      disabled={!isDrawable}
                      onClick={handleDraw}
                    />
                  ) : null}
                </StyledCardsWrapper>
              </>
            ) : (
              <>
                <StyledCardsWrapper>
                  <Button
                    text="Purchase"
                    disabled={!isPurchasable}
                    onClick={handlePurchase}
                  />
                  {account == selectedPool.owner || account == contractOwner ? (
                    <Button
                      text="Conclude the Sale"
                      disabled={!isDrawable}
                      onClick={handleDraw}
                    />
                  ) : null}
                </StyledCardsWrapper>
              </>
            )}
          </>
        ) : (
          <>
            <Label text="Sorry, the pool expired" />
            <Spacer size="md" />
            <StyledCardsWrapper>
              <Button
                text="Renew Pool"
                disabled={!isPurchasable}
                onClick={handleRenew}
              />
            </StyledCardsWrapper>
          </>
        )}
        <Spacer size="lg" />
        <StyledInfo>
          ⭐️ Every time you refer a sale, you&lsquo;ll get 1% commission of the
          order. If you referred the most sale in this launch when it closes,
          you&lsquo;d get 1% of its entire proceeds. {''}
          <StyledReferralLink
            onClick={() =>
              navigator.clipboard
                .writeText(`${window.location.href}?r=${account}`)
                .then(toast('Referral url has been copied to your paste bin'))
            }
          >
            Copy your referral link
          </StyledReferralLink>{' '}
          and refer someone now!
        </StyledInfo>
        <Spacer size="lg" />
      </StyledPool>
      <ToastContainer />
    </div>
  )
}

const StyledPool = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 700px;
  margin: auto;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const StyledButtonWrapper = styled.span`
  margin-right: 20px;
  width: 400px;
`

const StyledCardsWrapper = styled.span`
  display: flex;
  width: 600px;
  @media (max-width: 768px) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: center;
  }
`

const StyledInfo = styled.h3`
  color: ${(props) => props.theme.color.grey[400]};
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  text-align: left;
`

const StyledErrMessage = styled.h3`
  color: ${(props) => props.theme.color.primary.main};
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  text-align: left;
`

const StyledReferralLink = styled.span`
  color: ${(props) => props.theme.color.grey[500]};
  cursor: pointer;
  font-size: 20px;
  font-weight: 400;
  margin: 0;
  padding: 0;
`

export default Pool
